import client from '@/graphql/client';
import {
  BANK_PARSER_RULE_LIST,
  PAYMENT_INBOUND_MESSAGE_LIST,
  TEST_BANK_PARSER_REGEX,
  INBOUND_AGENT_LIST,
} from '@/graphql/queries/bankParserRuleQueries';
import {
  BANK_PARSER_RULE_UPSERT,
} from '@/graphql/mutations/bankParserRuleMutations';
import {
  BankParserRuleItem,
  BankParserRuleUpsertInput,
  TestBankParserRegexInput,
  TestBankParserRegexResult,
  PaymentInboundMessageItem,
  InboundMessageSearchFilters,
  InboundAgentItem,
} from '@/types/bankParserRule';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000';

/**
 * Fetch all bank parser rules (joined with DB Banks table)
 */
export async function getBankParserRules(): Promise<{ success: boolean; data: BankParserRuleItem[]; total: number }> {
  try {
    const response = await client.query({
      query: BANK_PARSER_RULE_LIST,
      fetchPolicy: 'network-only',
    });
    return response.data?.BankParserRuleList || { success: true, data: [], total: 0 };
  } catch (error) {
    // Fallback to REST endpoint
    try {
      const res = await fetch(`${API_URL}/api/v1/internal/payment/inbound/rules`);
      if (res.ok) {
        return await res.json();
      }
    } catch {
      // ignore
    }
    console.error('Error fetching bank parser rules:', error);
    throw error;
  }
}

/**
 * Upsert bank parser rule
 */
export async function saveBankParserRule(input: BankParserRuleUpsertInput): Promise<{ success: boolean; data: BankParserRuleItem; message?: string }> {
  try {
    const response = await client.mutate({
      mutation: BANK_PARSER_RULE_UPSERT,
      variables: { value: input },
    });
    return response.data?.BankParserRuleUpsert || { success: true, data: input as any };
  } catch (error) {
    // Fallback to REST
    try {
      const res = await fetch(`${API_URL}/api/v1/internal/payment/inbound/rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (res.ok) {
        return await res.json();
      }
    } catch {
      // ignore
    }
    console.error('Error saving bank parser rule:', error);
    throw error;
  }
}

/**
 * Test / simulate live regex parsing on raw text in the sandbox
 */
export async function testBankParserRegex(input: TestBankParserRegexInput): Promise<{ success: boolean; data: TestBankParserRegexResult }> {
  try {
    const response = await client.query({
      query: TEST_BANK_PARSER_REGEX,
      variables: { value: input },
      fetchPolicy: 'network-only',
    });
    return response.data?.TestBankParserRegex || { success: false, data: null as any };
  } catch (error) {
    // Fallback to REST
    try {
      const res = await fetch(`${API_URL}/api/v1/internal/payment/inbound/test-regex`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (res.ok) {
        return await res.json();
      }
    } catch {
      // ignore
    }
    console.error('Error simulating regex parse:', error);
    throw error;
  }
}

/**
 * Fetch inbound messages with filter and two-tab logic
 */
export async function getInboundMessages(
  filters: InboundMessageSearchFilters,
  page = 1,
  limit = 10
): Promise<{ success: boolean; data: PaymentInboundMessageItem[]; total: number; page: number; limit: number; totalPages: number }> {
  try {
    const variables: any = {
      tab: filters.tab || "matched",
      agentPrefix: filters.agentPrefix === 'all' ? undefined : filters.agentPrefix,
      bankCode: filters.bankCode === 'all' ? undefined : filters.bankCode,
      receiverBankCode: filters.receiverBankCode === 'all' ? undefined : filters.receiverBankCode,
      playerUsername: filters.playerUsername || undefined,
      status: filters.status === 'all' ? undefined : filters.status,
      search: filters.search || undefined,
      dateFrom: filters.dateFrom || undefined,
      dateTo: filters.dateTo || undefined,
      amountMin: filters.amountMin || undefined,
      amountMax: filters.amountMax || undefined,
      page,
      limit,
    };

    const response = await client.query({
      query: PAYMENT_INBOUND_MESSAGE_LIST,
      variables: { value: variables },
      fetchPolicy: 'network-only',
    });

    return response.data?.PaymentInboundMessageList || { success: true, data: [], total: 0, page, limit, totalPages: 1 };
  } catch (error) {
    // Fallback to REST
    try {
      const params = new URLSearchParams();
      params.set('tab', filters.tab || 'matched');
      params.set('page', String(page));
      params.set('limit', String(limit));
      if (filters.agentPrefix && filters.agentPrefix !== 'all') params.set('agentPrefix', filters.agentPrefix);
      if (filters.bankCode && filters.bankCode !== 'all') params.set('bankCode', filters.bankCode);
      if (filters.receiverBankCode && filters.receiverBankCode !== 'all') params.set('receiverBankCode', filters.receiverBankCode);
      if (filters.playerUsername) params.set('playerUsername', filters.playerUsername);
      if (filters.status && filters.status !== 'all') params.set('status', filters.status);
      if (filters.search) params.set('search', filters.search);
      if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.set('dateTo', filters.dateTo);
      const res = await fetch(`${API_URL}/api/v1/internal/payment/inbound/messages?${params.toString()}`);
      if (res.ok) {
        return await res.json();
      }
    } catch {
      // ignore
    }
    console.error('Error fetching inbound messages:', error);
    throw error;
  }
}

/**
 * Fetch all B2C agents configured for auto-deposit
 */
export async function getInboundAgents(): Promise<{ success: boolean; data: InboundAgentItem[] }> {
  try {
    const response = await client.query({
      query: INBOUND_AGENT_LIST,
      fetchPolicy: 'network-only',
    });
    return response.data?.InboundAgentList || { success: true, data: [] };
  } catch (error) {
    console.error('Error fetching inbound agents:', error);
    return { success: false, data: [] };
  }
}

